if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const orderList = this.querySelector('b2b-quick-order');
        if (orderList && (orderList.busy || orderList.pending?.size)) return;
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        const productURL = new URL(opener.getAttribute('data-product-url'), window.location.origin);
        if (this.hasAttribute('data-b2b')) productURL.searchParams.set('section_id', 'b2b-quick-order');

        fetch(productURL)
          .then((response) => {
            if (!response.ok) throw new Error('Unable to load product');
            return response.text();
          })
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            if (this.hasAttribute('data-b2b')) {
              const orderList = responseHTML.querySelector('b2b-quick-order');
              if (!orderList) throw new Error('Unable to load B2B order list');
              orderList.dataset.orderView = 'grid';
              this.classList.add('quick-add-modal--b2b');
              this.modalContent.replaceChildren(document.importNode(orderList, true));
              super.show(opener);
              return;
            }
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .catch(() => {
            window.location.assign(opener.getAttribute('data-product-url'));
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const productPopup = productElement.querySelector('.product-popup-modal__opener');
        if (productPopup) productPopup.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        const hTitle = productElement.querySelectorAll('h1');
        if (hTitle) hTitle.forEach(modal => modal.remove());

        const Accordion = productElement.querySelectorAll('.accordion');
        if (Accordion) Accordion.forEach(modal => modal.remove());

        const ProductDescription = productElement.querySelectorAll('.product-description-static');
        if (ProductDescription) ProductDescription.forEach(modal => modal.remove());

        const ProductDescriptionExpandButton = productElement.querySelectorAll('.product-text-clamp-toggle');
        if (ProductDescriptionExpandButton) ProductDescriptionExpandButton.forEach(modal => modal.remove());      
        
        const ProductTabs = productElement.querySelectorAll('.product-info-tabs');
        if (ProductTabs) ProductTabs.forEach(modal => modal.remove());

        const ProductBreadcrumb = productElement.querySelectorAll('.product-breadcrumb-block');
        if (ProductBreadcrumb) ProductBreadcrumb.forEach(modal => modal.remove());

        const SiblingProductSelectors = productElement.querySelectorAll('.product__cross-links');
        if (SiblingProductSelectors) SiblingProductSelectors.forEach(modal => modal.remove());      

        const SocialLinks = productElement.querySelectorAll('.product-share-component');
        if (SocialLinks) SocialLinks.forEach(modal => modal.remove());

        const ProductBadges = productElement.querySelectorAll('.main-product--badge');
        if (ProductBadges) ProductBadges.forEach(modal => modal.remove());       
        
        const ProductIcons = productElement.querySelectorAll('.product-feature-icon-grid--wrapper');
        if (ProductIcons) ProductIcons.forEach(modal => modal.remove());

        const ProductFeatureList = productElement.querySelectorAll('.product__feature-checklist');
        if (ProductFeatureList) ProductFeatureList.forEach(modal => modal.remove());        
        
        const ProductImgBlocks = productElement.querySelectorAll('.product__inset_image_block');
        if (ProductImgBlocks) ProductImgBlocks.forEach(modal => modal.remove()); 

      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

    }
  );
}

if (!customElements.get('b2b-quick-order')) {
  customElements.define('b2b-quick-order', class extends HTMLElement {
    static cartQueue = Promise.resolve();
    static cartRead = null;

    subscribeToCart() {
      this.cartUnsubscribe?.();
      this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
        try {
          const cart = Array.isArray(event.cartData?.items) && typeof event.cartData?.item_count === 'number'
            ? event.cartData : await this.loadCart();
          if (this.isConnected) this.applyCart(cart);
        } catch (_) {
          this.error.textContent = this.dataset.error;
          this.error.hidden = false;
        }
      });
    }

    connectedCallback() {
      this.observeQuantityLabels();
      if (this.initialized) {
        this.subscribeToCart();
        this.ready = this.loadCart().then((cart) => this.applyCart(cart)).catch(() => {
          this.error.textContent = this.dataset.error;
          this.error.hidden = false;
        });
        return;
      }
      this.initialized = true;
      this.pending = new Map();
      this.form = this.querySelector('form');
      this.error = this.querySelector('[role="alert"]');
      this.status = this.querySelector('[role="status"]');
      this.money = new Intl.NumberFormat(this.dataset.locale, {
        style: 'currency', currency: this.dataset.currency
      });
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.flush(); });
      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-variant-id]')) this.queue(event.target);
      });
      this.addEventListener('click', async (event) => {
        const link = event.target.closest('[data-view-cart]');
        if (link) {
          event.preventDefault();
          const lists = Array.from(document.querySelectorAll('b2b-quick-order'));
          await Promise.all(lists.map((list) => list.flush()));
          if (lists.every((list) => list.error.hidden)) window.location.assign(link.href);
          return;
        }
        const button = event.target.closest('[data-adjust]');
        if (!button) return;
        const input = button.parentElement.querySelector('input');
        const current = Number(input.value) || 0;
        const increment = Number(input.dataset.increment);
        const minimum = Number(input.dataset.min);
        const next = Number(button.dataset.adjust) > 0
          ? (current === 0 ? minimum : current + increment)
          : (current <= minimum ? 0 : current - increment);
        if (input.hasAttribute('max') && next > Number(input.max)) return;
        input.value = next;
        this.queue(input);
      });
      this.updateTotals();
      this.loadRetailPrices();
      this.subscribeToCart();
      this.ready = this.loadCart().then((cart) => this.applyCart(cart)).catch(() => {
        this.error.textContent = this.dataset.error;
        this.error.hidden = false;
      });
    }

    observeQuantityLabels() {
      this.quantityLabelObserver?.disconnect();
      const table = this.querySelector('table');
      this.quantityLabelObserver = new ResizeObserver(() => {
        const widths = Array.from(this.querySelectorAll('[data-quantity-label]'), (label) => {
          const cell = label.closest('th, td');
          const style = getComputedStyle(cell);
          return label.getBoundingClientRect().width + parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
        });
        const tableWidth = table.getBoundingClientRect().width;
        if (!tableWidth) return;
        const quantityWidth = Math.max(tableWidth * 0.15, Math.ceil(Math.max(132, ...widths)));
        const hasRrp = Boolean(this.dataset.rrpCountry);
        this.style.setProperty('--quantity-column-width', `${quantityWidth}px`);
        this.style.setProperty('--variant-column-width', `${tableWidth * (hasRrp ? 0.55 : 0.6) - quantityWidth}px`);
        this.style.setProperty('--price-column-width', `${tableWidth * (hasRrp ? 0.15 : 0.2)}px`);
      });
      this.quantityLabelObserver.observe(table);
      this.querySelectorAll('[data-quantity-label]').forEach((label) => this.quantityLabelObserver.observe(label));
    }

    async loadRetailPrices() {
      const country = this.dataset.rrpCountry;
      const rows = Array.from(this.querySelectorAll('[data-variant-row][data-variant-gid]'));
      if (!country || !rows.length) return;
      const ids = rows.map((row) => row.dataset.variantGid);
      try {
        const response = await fetch('/api/2026-07/graphql.json', {
          method: 'POST',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            query: `query VariantRetailPrices($ids: [ID!]!, $country: CountryCode!) @inContext(country: $country) {
              nodes(ids: $ids) {
                ... on ProductVariant { id price { amount currencyCode } }
              }
            }`,
            variables: { ids, country }
          })
        });
        const result = await response.json();
        if (!response.ok || result.errors) throw new Error('Unable to load retail prices');
        const prices = new Map(result.data.nodes.filter(Boolean).map((node) => [node.id, node.price]));
        const formatters = new Map();
        rows.forEach((row) => {
          const price = prices.get(row.dataset.variantGid);
          const cell = row.querySelector('[data-rrp-price]');
          if (!price || !cell) return;
          if (!formatters.has(price.currencyCode)) {
            formatters.set(price.currencyCode, new Intl.NumberFormat(this.dataset.locale, {
              style: 'currency', currency: price.currencyCode
            }));
          }
          cell.textContent = formatters.get(price.currencyCode).format(Number(price.amount));
        });
      } catch (_) {
        // Keep the non-blocking placeholder if retail pricing can't be loaded.
      }
    }

    disconnectedCallback() {
      this.quantityLabelObserver?.disconnect();
      this.cartUnsubscribe?.();
      clearTimeout(this.timer);
      if (this.pending?.size) this.flush();
    }

    queue(input) {
      this.updateTotals();
      if (!input.reportValidity()) {
        this.pending.delete(input.dataset.variantId);
        return;
      }
      this.error.hidden = true;
      this.pending.set(input.dataset.variantId, Number(input.value));
      this.status.textContent = this.dataset.saving;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.flush(), 350);
    }

    updateTotals(cart) {
      let count = 0;
      let subtotal = 0;
      this.querySelectorAll('[data-variant-row]').forEach((row) => {
        const input = row.querySelector('input');
        const quantity = input ? Number(input.value) : 0;
        const validQuantity = Number.isInteger(quantity) && quantity >= 0;
        const validRule = !quantity || (quantity >= Number(input.dataset.min) &&
          quantity % Number(input.dataset.increment) === 0 &&
          (!input.dataset.max || quantity <= Number(input.dataset.max)));
        if (input) input.setCustomValidity(validQuantity && validRule ? '' : this.dataset.quantityError);
        let price = Number(row.dataset.price);
        JSON.parse(row.dataset.breaks).forEach((tier) => {
          if (quantity >= tier.minimum_quantity) price = Math.min(price, Number(tier.price));
        });
        const selected = validQuantity ? quantity : 0;
        const lines = cart?.items.filter((item) => String(item.variant_id) === input?.dataset.variantId);
        const total = lines ? lines.reduce((sum, item) => sum + item.final_line_price, 0) : price * selected;
        if (lines?.length && selected) price = total / selected;
        row.querySelector('[data-unit-price]').textContent = this.money.format(price / 100);
        row.querySelector('[data-row-total]').textContent = this.money.format(total / 100);
        count += selected;
        subtotal += total;
        const buttons = row.querySelectorAll('[data-adjust]');
        if (input && buttons.length) {
          buttons[0].disabled = selected === 0;
          buttons[1].disabled = input.dataset.available === 'false' || (input.hasAttribute('max') && selected >= Number(input.max));
        }
      });
      this.querySelector('[data-total-items]').textContent = count;
      this.querySelector('[data-subtotal]').textContent = this.money.format(subtotal / 100);
    }

    async loadCart() {
      const controller = this.constructor;
      if (!controller.cartRead) {
        controller.cartRead = (async () => {
          const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(this.dataset.error);
          return response.json();
        })().finally(() => { controller.cartRead = null; });
      }
      return controller.cartRead;
    }

    applyCart(cart) {
      const bagItems = this.querySelector('[data-bag-items]');
      const bagTotal = this.querySelector('[data-bag-total]');
      if (bagItems) bagItems.textContent = cart.item_count;
      if (bagTotal) bagTotal.textContent = this.money.format(cart.total_price / 100);
      this.querySelectorAll('[data-variant-id]').forEach((input) => {
        if (!this.pending.has(input.dataset.variantId)) {
          input.value = cart.items.filter((item) => String(item.variant_id) === input.dataset.variantId)
            .reduce((sum, item) => sum + item.quantity, 0);
        }
      });
      this.updateTotals(this.pending.size ? null : cart);
    }

    refreshCartSections(sections, cart) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections?.['cart-icon-bubble']) {
        const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        bubble.innerHTML = html.querySelector('.shopify-section').innerHTML;
      }
      const drawer = document.querySelector('cart-drawer');
      if (drawer && sections?.['cart-drawer']) {
        const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const content = html.querySelector('#CartDrawer');
        if (content) {
          drawer.querySelector('#CartDrawer').innerHTML = content.innerHTML;
          drawer.classList.toggle('is-empty', cart.item_count === 0);
          drawer.querySelector('#CartDrawer-Overlay').addEventListener('click', () => drawer.close());
        }
      }
    }

    async flush() {
      clearTimeout(this.timer);
      if (this.saving) return this.saving;
      if (!this.pending.size) return;
      this.saving = this.constructor.cartQueue.then(() => this.savePending());
      this.constructor.cartQueue = this.saving.catch(() => {});
      try {
        await this.saving;
      } finally {
        this.saving = null;
      }
      if (this.pending.size) await this.flush();
    }

    async savePending() {
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.updateTotals();
      let cart;
      try {
        await this.ready;
        cart = await this.loadCart();
        while (this.pending.size) {
          const [id, quantity] = this.pending.entries().next().value;
          const lines = cart.items.filter((item) => String(item.variant_id) === id);
          if (lines.length > 1 || lines.some((item) => item.selling_plan_allocation || Object.keys(item.properties || {}).length)) {
            throw new Error(this.dataset.error);
          }
          const existing = lines[0];
          if (existing || quantity > 0) {
            const previousCart = cart;
            const response = await fetch(existing ? routes.cart_change_url : routes.cart_add_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                ...(existing ? { id: existing.key, quantity } : { items: [{ id, quantity }] }),
                sections: ['cart-icon-bubble', 'cart-drawer'], sections_url: window.location.pathname
              })
            });
            const result = await response.json();
            if (!response.ok || result.status) throw new Error(result.description || this.dataset.error);
            cart = existing ? result : await this.loadCart();
            cart = await this.trackOrderView(cart, previousCart, quantity > (existing?.quantity || 0));
            this.refreshCartSections(result.sections, cart);
          }
          if (this.pending.get(id) === quantity) this.pending.delete(id);
          this.applyCart(cart);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
        }
        this.status.textContent = this.dataset.saved.replace('[count]', cart.item_count);
      } catch (error) {
        this.pending.clear();
        this.status.textContent = '';
        this.error.textContent = error.message || this.dataset.error;
        this.error.hidden = false;
        try { cart = await this.loadCart(); } catch (_) { cart = null; }
        try {
          const response = await fetch(`${routes.cart_url}?sections=cart-icon-bubble,cart-drawer`);
          if (response.ok && cart) this.refreshCartSections(await response.json(), cart);
        } catch (_) {}
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        if (cart) this.applyCart(cart);
        else this.updateTotals();
      }
    }

    async trackOrderView(cart, previousCart, increased) {
      const view = this.dataset.orderView;
      if (view !== 'list' && view !== 'grid') return cart;
      const key = 'b2b_order_view';
      const previous = previousCart.item_count ? previousCart.attributes?.[key] : '';
      let value = previous || '';
      if (!cart.item_count) value = '';
      else if (increased) value = previous && previous !== view ? 'mixed' : view;
      if (value === (cart.attributes?.[key] || '')) return cart;
      try {
        const response = await fetch(routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { [key]: value } })
        });
        const updatedCart = await response.json();
        if (!response.ok || updatedCart.status) throw new Error('Unable to record B2B order view');
        return updatedCart;
      } catch (error) {
        // Attribution failure must not undo a successful quantity change.
        console.warn('Unable to record B2B order view', error);
        return cart;
      }
    }
  });
}
